// frontend/src/components/profile/LogisticsDashboard.js
import React, { useState, useEffect } from 'react';
import { deliveryApi } from '../../api/delivery';
import uploadService from '../../services/upload';
import { compressImage } from '../../utils/imageCompression';
import { FaTruck, FaBoxOpen, FaCheckCircle, FaMapMarkerAlt, FaPhone, FaCamera, FaSpinner, FaTimes } from 'react-icons/fa';

function LogisticsDashboard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // active, history

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [proofImage, setProofImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const data = await deliveryApi.getMyJobs();
      setJobs(data);
    } catch (err) {
      console.error("Failed to load jobs", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (jobId, status) => {
      if (!window.confirm(`Mark this order as ${status.replace('_', ' ').toUpperCase()}?`)) return;
      try {
          await deliveryApi.updateDeliveryStatus(jobId, status);
          fetchJobs();
      } catch (err) {
          alert("Failed to update status.");
      }
  };

  const openCompleteModal = (job) => {
      setSelectedJob(job);
      setProofImage(null);
      setNotes('');
      setIsModalOpen(true);
  };

  const handleImageSelect = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setUploading(true);
      try {
          const compressed = await compressImage(file, 0.5);
          const res = await uploadService.uploadImage(compressed, 'proof-of-delivery');
          setProofImage(res.url);
      } catch (err) {
          alert("Failed to upload image.");
      } finally {
          setUploading(false);
      }
  };

  const handleCompleteDelivery = async (e) => {
      e.preventDefault();
      if (!proofImage) return alert("Proof of Delivery image is required.");
      
      try {
          await deliveryApi.updateDeliveryStatus(selectedJob.id, 'delivered', notes, proofImage);
          alert("Delivery Completed Successfully!");
          setIsModalOpen(false);
          fetchJobs();
      } catch (err) {
          alert("Failed to complete delivery.");
      }
  };

  const filteredJobs = jobs.filter(job => {
      if (activeTab === 'active') return job.status !== 'delivered' && job.status !== 'failed';
      return job.status === 'delivered' || job.status === 'failed';
  });

  if (loading) return <div className="text-center py-10">Loading jobs...</div>;

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <FaTruck className="text-primary" /> Logistics Dashboard
      </h2>

      {/* TABS */}
      <div className="flex gap-4 mb-6 border-b border-gray-200">
          <button 
              onClick={() => setActiveTab('active')}
              className={`pb-2 px-4 font-medium ${activeTab === 'active' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
          >
              Active Deliveries
          </button>
          <button 
              onClick={() => setActiveTab('history')}
              className={`pb-2 px-4 font-medium ${activeTab === 'history' ? 'border-b-2 border-primary text-primary' : 'text-gray-500'}`}
          >
              Delivery History
          </button>
      </div>

      {filteredJobs.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 rounded border border-dashed">
              <p className="text-gray-500">No {activeTab} jobs found.</p>
          </div>
      ) : (
          <div className="space-y-4">
              {filteredJobs.map(job => (
                  <div key={job.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                      <div className="flex justify-between items-start mb-3">
                          <div>
                              <h3 className="font-bold text-gray-800 text-lg">Order #{job.order_number}</h3>
                              <span className={`text-xs px-2 py-1 rounded font-bold uppercase ${
                                  job.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                                  job.status === 'picked_up' ? 'bg-yellow-100 text-yellow-800' :
                                  job.status === 'in_transit' ? 'bg-purple-100 text-purple-800' :
                                  'bg-green-100 text-green-800'
                              }`}>
                                  {job.status.replace('_', ' ')}
                              </span>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                              {new Date(job.assigned_at).toLocaleDateString()}
                          </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 text-sm">
                          <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs font-bold text-gray-500 uppercase mb-1">Customer</p>
                              <p className="font-bold">{job.customer_name}</p>
                              <p className="flex items-center gap-2 text-gray-600"><FaPhone size={12} /> {job.customer_phone}</p>
                          </div>
                          <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs font-bold text-gray-500 uppercase mb-1">Destination</p>
                              <p className="flex items-start gap-2 text-gray-800">
                                  <FaMapMarkerAlt className="mt-1 text-red-500 flex-shrink-0" />
                                  {job.delivery_address}
                              </p>
                          </div>
                      </div>

                      {/* ACTIONS */}
                      {job.status !== 'delivered' && (
                          <div className="flex gap-2 border-t pt-3">
                              {job.status === 'assigned' && (
                                  <button 
                                      onClick={() => handleStatusUpdate(job.id, 'picked_up')}
                                      className="flex-1 bg-yellow-500 text-white py-2 rounded font-bold hover:bg-yellow-600 flex items-center justify-center gap-2"
                                  >
                                      <FaBoxOpen /> Confirm Pickup
                                  </button>
                              )}
                              {job.status === 'picked_up' && (
                                  <button 
                                      onClick={() => handleStatusUpdate(job.id, 'in_transit')}
                                      className="flex-1 bg-purple-600 text-white py-2 rounded font-bold hover:bg-purple-700 flex items-center justify-center gap-2"
                                  >
                                      <FaTruck /> Start Transit
                                  </button>
                              )}
                              {(job.status === 'in_transit' || job.status === 'picked_up') && (
                                  <button 
                                      onClick={() => openCompleteModal(job)}
                                      className="flex-1 bg-green-600 text-white py-2 rounded font-bold hover:bg-green-700 flex items-center justify-center gap-2"
                                  >
                                      <FaCheckCircle /> Complete Delivery
                                  </button>
                              )}
                          </div>
                      )}
                      
                      {job.status === 'delivered' && job.proof_of_delivery_url && (
                          <div className="mt-2">
                              <a href={job.proof_of_delivery_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                  <FaCheckCircle /> View Proof of Delivery
                              </a>
                          </div>
                      )}
                  </div>
              ))}
          </div>
      )}

      {/* --- COMPLETE DELIVERY MODAL --- */}
      {isModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                  <div className="flex justify-between items-center mb-4 border-b pb-2">
                      <h3 className="text-lg font-bold">Complete Delivery</h3>
                      <button onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <form onSubmit={handleCompleteDelivery} className="space-y-4">
                      <div className="bg-blue-50 p-3 rounded text-sm text-blue-800">
                          Please upload a photo of the package at the destination or the customer signature.
                      </div>

                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                          {uploading ? (
                              <div className="text-primary flex flex-col items-center">
                                  <FaSpinner className="animate-spin text-2xl mb-2" />
                                  Uploading...
                              </div>
                          ) : proofImage ? (
                              <div className="relative">
                                  <img src={proofImage} alt="Proof" className="max-h-40 mx-auto rounded" />
                                  <button 
                                      type="button" 
                                      onClick={() => setProofImage(null)}
                                      className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full text-xs"
                                  >
                                      <FaTimes />
                                  </button>
                              </div>
                          ) : (
                              <label className="cursor-pointer flex flex-col items-center text-gray-500 hover:text-primary">
                                  <FaCamera className="text-3xl mb-2" />
                                  <span>Click to Upload Proof</span>
                                  <input type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
                              </label>
                          )}
                      </div>

                      <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1">Delivery Notes (Optional)</label>
                          <textarea 
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              className="w-full p-2 border rounded"
                              placeholder="e.g. Left with security guard..."
                          ></textarea>
                      </div>

                      <button 
                          type="submit" 
                          disabled={!proofImage}
                          className={`w-full py-2 rounded font-bold text-white ${proofImage ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}
                      >
                          Confirm Delivery
                      </button>
                  </form>
              </div>
          </div>
      )}

    </div>
  );
}

export default LogisticsDashboard;